/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import type { FunctionComponent } from 'react';

import { IconButton, Typography2, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { PersonalizedMessage } from 'bundles/degree-description/utils/personalizedMessageContent';
import { incrementClosedPersonalizedMessageCount } from 'bundles/degree-description/utils/personalizedMessageViews';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/degree-description';

const styles = {
  tooltipWrap: (theme: Theme) => css`
    background-color: var(--cds-color-white-0);
    border-radius: 6px;
    box-shadow: 0 2px 12px 0 rgb(0 0 0 / 24%);
    margin-top: 28px;
    max-width: 350px;
    padding: ${theme.spacing(16)};
    position: absolute;
    right: 50%;
    transform: translateX(250px) translateY(80px);
    z-index: 30;

    ${breakpoints.down('md')} {
      right: 48%;
      transform: translateX(150px) translateY(80px);
    }

    @media screen and (max-width: 1454px) and (min-width: 1141px) {
      right: 48%;
      transform: translateX(180px) translateY(80px);
    }
    ${breakpoints.down('sm')} {
      display: none;
    }

    &::after {
      border-bottom: 15px solid transparent;
      border-top: 15px solid transparent;
      border-left: 21px solid var(--cds-color-white-0);
      content: '';
      height: 0;
      margin: -15px -20px 0 0;
      position: absolute;
      right: 0;
      top: 50%;
      width: 0;
    }
  `,
  xddpTooltip: css`
    right: 50%;
    transform: translateX(200px) translateY(100px);

    ${breakpoints.down('md')} {
      transform: translateX(150px) translateY(100px);
    }

    @media screen and (max-width: 1454px) and (min-width: 1141px) {
      right: 50%;
      transform: translateX(150px) translateY(100px);
    }
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  degreeDescriptionTooltip: css`
    right: 50%;
    transform: translateX(200px) translateY(150px);
    z-index: 20;

    ${breakpoints.down('md')} {
      right: 48%;
      transform: translateX(120px) translateY(150px);
    }

    @media screen and (max-width: 1454px) and (min-width: 1141px) {
      right: 48%;
      transform: translateX(130px) translateY(150px);
    }
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  tooltipHeader: css`
    max-width: calc(100% - 18px);
    width: 100%;
  `,
  closeButton: (theme: Theme) => css`
    position: absolute;
    right: 0;
    top: ${theme.spacing(8)};
  `,
};

type Props = {
  slug?: string | null;
  personalizedMessage?: PersonalizedMessage | null;
  onXddp?: boolean;
};

const PersonalizedTooltip: FunctionComponent<Props> = ({ slug, personalizedMessage, onXddp }) => {
  const [isOpen, setOpen] = useState(true);

  if (!personalizedMessage || !slug) return null;

  const handleClose = () => {
    incrementClosedPersonalizedMessageCount(slug, personalizedMessage.id);
    setOpen(false);
  };

  const { title, body } = personalizedMessage;

  return isOpen ? (
    <TrackedDiv
      trackingName="personalized_message_tooltip"
      trackClicks={false}
      data={personalizedMessage}
      withVisibilityTracking
      className="rc-PersonalizedTooltip"
      css={[styles.tooltipWrap, onXddp ? styles.xddpTooltip : styles.degreeDescriptionTooltip]}
    >
      <Typography2 variant="subtitleMedium" component="h2" css={styles.tooltipHeader}>
        {title}
      </Typography2>
      <Typography2 component="p">{body}</Typography2>
      <IconButton
        component={TrackedButton}
        data={personalizedMessage}
        trackingName="personalized_message_tooltip_close"
        withVisibilityTracking={false}
        requireFullyVisible={false}
        css={styles.closeButton}
        variant="ghost"
        intent="close"
        size="small"
        onClick={handleClose}
        aria-label={_t('Dismiss tooltip')}
      />
    </TrackedDiv>
  ) : null;
};

export default PersonalizedTooltip;
